import React, {
  useEffect,
  useState,
} from 'react'

import PropTypes from 'prop-types'

import noop from 'utils/noop'

import { useGVL } from '../GVLProvider'

import TCFContext from './TCFContext'

// Websites that do not run TCF get these, and so does a website whose TCF
// chunk fails to arrive.
const NO_TCF = {
  acceptAll: noop,
  declineAll: noop,
  getStoredTCString: noop,
  gvl: void 0,
  makeTCString: noop,
  saveConsent: noop,
  tcModel: void 0,
}


export default function TCFProvider({ children, lang }) {
  const { gvl } = useGVL()

  const [value, setValue] = useState()

  useEffect(() => {
    if ( !gvl ) {
      console.debug('[Termly] No GVL found for this website. TCFProvider is providing noop functions.')

      return
    }

    // `window.displayPreferenceModal()` renders PreferencesApp into the same
    // root element that ResourceBlockerApp occupies, which unmounts this
    // provider. The signal stops a chunk that arrives after that from setting
    // state on the unmounted tree.
    const controller = new AbortController()

    // Rendering the children against `NO_TCF` while this is in flight would
    // paint the non-TCF banner to a TCF visitor and then swap it, so we hold
    // the first paint below instead. GVLProvider already does the same thing
    // while it fetches the vendor list.
    import('./lib/makeTCFValue')
      .then(({ default: makeTCFValue }) => makeTCFValue({ gvl, lang }))
      .catch((error) => {
        console.error('[Termly] The IAB TCF libraries failed to load, so this page cannot '
                      + 'collect TCF consent. Try reloading the page. If that does not work, '
                      + 'please contact Termly for help: https://termly.io/contact-us\n\n%s',
                      error)

        return NO_TCF
      })
      .then((nextValue) => {
        if ( controller.signal.aborted ) {
          return
        }

        setValue(nextValue)
      })

    return () => {
      controller.abort()
    }
  }, [gvl, lang])

  if ( !gvl ) {
    return (
      <TCFContext.Provider
        value={ NO_TCF }
      >
        { children }
      </TCFContext.Provider>
    )
  }

  if ( !value ) {
    return null
  }

  return (
    <TCFContext.Provider
      value={ value }
    >
      { children }
    </TCFContext.Provider>
  )
}

TCFProvider.propTypes = {
  children: PropTypes.node,
  lang: PropTypes.string,
}
